import { useNavigate } from "react-router-dom";
import { AlertCircle, ArrowLeft } from "lucide-react";
import { isProblemType } from "@iamu/shared";
import { Page } from "@/components/layout/page";
import {
  EntityNotFound,
  EntityErrorState,
} from "@/components/layout/entity-state";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tonal } from "@/components/ui/tonal";
import { Kicker } from "@/components/ui/kicker";
import { MarkdownProse } from "@/components/ui/markdown-prose";
import { FindingKicker } from "@/components/experience/finding-kicker";
import { FindingPolarityMark } from "@/components/experience/finding-polarity-mark";
import { FindingObservation } from "@/components/experience/finding-observation";
import { WorkRequestChip } from "@/components/experience/work-request-chip";
import { SupersedeRequestDialog } from "@/components/experience/supersede-request-dialog";
import {
  PriorityChip,
  PriorityExplanation,
} from "@/components/triage/priority-chip";
import { ActivityTimeline } from "@/components/workflow/activity-timeline";
import { useFindingDetail } from "@/components/finding-detail/use-finding-detail";
import { FindingDetailActions } from "@/components/finding-detail/finding-detail-actions";
import { FindingInspectorRail } from "@/components/finding-detail/finding-inspector-rail";
import { FindingEvidenceSection } from "@/components/finding-detail/finding-evidence-section";
import { FindingCitationsSection } from "@/components/finding-detail/finding-citations-section";
import { FindingOccurrencesSection } from "@/components/finding-detail/finding-occurrences-section";
import { FindingFixHistorySection } from "@/components/finding-detail/finding-fix-history-section";

export function FindingDetailPage() {
  const navigate = useNavigate();
  const detail = useFindingDetail();

  const {
    projectId,
    findingId,
    isValidId,
    data,
    isLoading,
    error,
    refetch,
    isCanonical,
    back,
    exportConfig,
    workflow,
    gateHistory,
    requests,
    transition,
    title,
    description,
    uiFact,
    consequence,
    what,
    why,
    basis,
    screen,
    breaksRule,
    breaksFile,
    scenarioName,
    scenarioId,
    personaName,
    personaRole,
    personaIdForLink,
    severity,
    type,
    recommendation,
    state,
    stateReason,
    status,
    triageNoteFromData,
    observationCount,
    observations,
    evidence,
    citations,
    fixStatus,
    fixAttempts,
    verificationResults,
    hasLinkedRepo,
    canonicalParent,
    firstSeenFormatted,
    priorityData,
    openRequest,
  } = detail;

  if (!projectId || !findingId) {
    return (
      <Page title="Finding">
        <div className="flex flex-col items-center gap-3 py-12 text-center">
          <AlertCircle
            className="h-10 w-10 text-destructive"
            aria-hidden="true"
          />
          <p className="text-base font-medium">Invalid finding URL</p>
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Go back
          </Button>
        </div>
      </Page>
    );
  }

  if (isLoading) {
    return (
      <Page title="Finding" back={back} maxWidth="max-w-[820px]">
        <div className="space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </Page>
    );
  }

  if (!isValidId || error?.data?.code === "NOT_FOUND" || !data) {
    return (
      <EntityNotFound
        title="Finding not found"
        entityName="finding"
        maxWidth="max-w-[820px]"
        backLink={{
          to: back.to,
          label: `Back to ${back.label}`,
        }}
      />
    );
  }

  if (error) {
    return (
      <EntityErrorState
        title="Failed to load finding"
        maxWidth="max-w-[820px]"
        onRetry={() => refetch()}
        backLink={{
          to: back.to,
          label: `Back to ${back.label}`,
        }}
      />
    );
  }

  return (
    <Page
      title={title}
      eyebrow={
        <div className="flex flex-wrap items-center gap-2">
          {priorityData && (
            <PriorityChip
              canonicalId={data.id as string}
              priority={priorityData}
            />
          )}
          <FindingKicker
            severity={severity}
            type={type}
            state={state}
            stateReason={stateReason}
            status={status}
            fixStatus={fixStatus}
          />
          {openRequest && <WorkRequestChip request={openRequest} />}
        </div>
      }
      leftMark={
        <FindingPolarityMark type={type} severity={severity} size="lg" />
      }
      maxWidth="max-w-[1240px]"
      back={back}
      actions={
        <FindingDetailActions
          isCanonical={isCanonical}
          type={type}
          openRequest={openRequest}
          state={state}
          findingId={data.id as string}
          projectId={projectId}
          hasLinkedRepo={hasLinkedRepo}
          requests={requests}
          transition={transition}
          title={title}
        />
      }
    >
      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-start">
        <div className="min-w-0 space-y-8">
          {/* Observation narrative */}
          <section className="space-y-3">
            <Kicker as="h2">Observation</Kicker>
            <FindingObservation
              uiFact={uiFact}
              consequence={consequence}
              what={what}
              why={why}
              basis={basis}
              screen={screen}
              breaksRule={breaksRule}
              breaksFile={breaksFile}
              description={description}
            />
          </section>

          {/* Where it happened — screenshot & step context */}
          {evidence && (
            <FindingEvidenceSection
              projectId={projectId}
              evidence={evidence}
              title={title}
              severity={severity}
              type={type}
              personaName={personaName}
              personaRole={personaRole}
            />
          )}

          {/* Triage note (if set) */}
          {isCanonical && triageNoteFromData && (
            <p className="text-sm text-muted-foreground">
              Triage: {triageNoteFromData}
            </p>
          )}

          {/* Suggested fix */}
          {recommendation && (
            <section className="space-y-3">
              <Kicker as="h2">Suggested fix</Kicker>
              <Tonal className="max-w-3xl">
                <MarkdownProse
                  content={recommendation}
                  lineBreaks
                  className="text-base leading-relaxed text-foreground"
                />
              </Tonal>
            </section>
          )}

          {/* Why it matters — citations as footnoted prose */}
          <FindingCitationsSection
            projectId={projectId}
            citations={citations}
          />

          {/* Where it showed up & Quality gate history */}
          {isCanonical && (
            <FindingOccurrencesSection
              projectId={projectId}
              observations={observations}
              gateHistory={gateHistory as any}
            />
          )}

          {/* Why it ranks here */}
          {isCanonical && isProblemType(type) && (
            <section className="space-y-3">
              <Kicker as="h2">Why it ranks here</Kicker>
              <div className="rounded-lg border border-border bg-card p-5 max-w-3xl">
                <PriorityExplanation canonicalId={data.id as string} />
              </div>
            </section>
          )}

          {/* Fix history — raw observation only */}
          {!isCanonical && (
            <FindingFixHistorySection
              fixAttempts={fixAttempts as any}
              verificationResults={verificationResults as any}
            />
          )}

          {/* Activity Timeline */}
          {isCanonical && (
            <ActivityTimeline
              canonicalId={data.id as string}
              projectId={projectId}
              canComment={workflow?.canEdit ?? false}
            />
          )}
        </div>

        {/* Right Sidebar Inspector */}
        <FindingInspectorRail
          id={projectId}
          findingId={data.id as string}
          isCanonical={isCanonical}
          severity={severity}
          type={type}
          observationCount={observationCount}
          firstSeenFormatted={firstSeenFormatted}
          scenarioName={scenarioName}
          scenarioId={scenarioId}
          personaName={personaName}
          personaIdForLink={personaIdForLink}
          personaRole={personaRole}
          stepNumber={evidence?.stepNumber}
          walkthroughId={evidence?.walkthroughId}
          canonicalParentId={canonicalParent?.id}
          stateReason={stateReason}
          fixStatus={fixStatus}
          hasLinkedRepo={hasLinkedRepo}
          exportConfig={exportConfig}
          state={state}
          subject={title}
        />
      </div>
      <SupersedeRequestDialog requests={requests} />
    </Page>
  );
}
